/**
 * JDExportModal - Generates and previews/downloads a job description in Markdown, PDF, or Word.
 * Supports optional infographic embedding (PDF/DOCX) with on-demand generation.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  exportJdPreview,
  exportJdBlob,
  generatePositionInfographic,
  JdExportOptions,
  JdFormat,
} from '../../services/teamApi';

type Template = 'standard' | 'compact' | 'detailed';

const FORMAT_LABELS: Record<JdFormat, string> = {
  md: 'Markdown',
  pdf: 'PDF',
  docx: 'Word',
};

interface JDExportModalProps {
  teamId: number;
  positionId: number;
  roleName: string;
  hasInfographic: boolean;
  onClose: () => void;
  onInfographicGenerated?: () => void;
}

export const JDExportModal: React.FC<JDExportModalProps> = ({
  teamId,
  positionId,
  roleName,
  hasInfographic: initialHasInfographic,
  onClose,
  onInfographicGenerated,
}) => {
  const [markdown, setMarkdown] = useState('');
  const [suggestedFilename, setSuggestedFilename] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [template, setTemplate] = useState<Template>('standard');
  const [format, setFormat] = useState<JdFormat>('md');
  const [includeInfographic, setIncludeInfographic] = useState(false);
  const [hasInfographic, setHasInfographic] = useState(initialHasInfographic);
  const [generatingInfographic, setGeneratingInfographic] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchPreview = useCallback(async (tmpl: Template) => {
    setLoading(true);
    setError('');
    try {
      const opts: JdExportOptions = { template: tmpl, format: 'md' };
      const result = await exportJdPreview(teamId, positionId, opts);
      setMarkdown(result.markdown);
      setSuggestedFilename(result.suggested_filename);
    } catch (err: any) {
      setError(err.message || 'Failed to generate JD preview');
    } finally {
      setLoading(false);
    }
  }, [teamId, positionId]);

  useEffect(() => {
    fetchPreview(template);
  }, [fetchPreview, template]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = markdown;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const triggerBlobDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownload = async () => {
    if (format === 'md') {
      const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
      triggerBlobDownload(blob, suggestedFilename || 'job_description.md');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const opts: JdExportOptions = {
        template,
        format,
        include_infographic: includeInfographic && hasInfographic,
      };
      const result = await exportJdBlob(teamId, positionId, opts);
      triggerBlobDownload(result.blob, result.suggested_filename);
    } catch (err: any) {
      setError(err.message || `Failed to export as ${format.toUpperCase()}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInfographic = async () => {
    setGeneratingInfographic(true);
    try {
      await generatePositionInfographic(teamId, positionId);
      setHasInfographic(true);
      setIncludeInfographic(true);
      onInfographicGenerated?.();
    } catch (err: any) {
      setError(err.message || 'Failed to generate infographic');
    } finally {
      setGeneratingInfographic(false);
    }
  };

  const showInfographicOption = format === 'pdf' || format === 'docx';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-dark-card border border-dark-border rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border">
          <div>
            <h3 className="text-lg font-bold text-dark-text">Export Job Description</h3>
            <p className="text-sm text-gray-400 mt-0.5">{roleName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-gray-700 text-gray-400 hover:text-gray-200 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Controls row */}
        <div className="flex flex-wrap items-center gap-4 px-6 py-3 border-b border-dark-border">
          {/* Format selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Format:</span>
            {(['md', 'pdf', 'docx'] as JdFormat[]).map(f => (
              <button
                key={f}
                onClick={() => setFormat(f)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                  format === f
                    ? 'bg-indigo-600 text-white'
                    : 'bg-dark-bg border border-dark-border text-gray-400 hover:text-gray-200'
                }`}
              >
                {FORMAT_LABELS[f]}
              </button>
            ))}
          </div>

          {/* Template selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Template:</span>
            {(['standard', 'compact', 'detailed'] as Template[]).map(t => (
              <button
                key={t}
                onClick={() => setTemplate(t)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                  template === t
                    ? 'bg-indigo-600 text-white'
                    : 'bg-dark-bg border border-dark-border text-gray-400 hover:text-gray-200'
                }`}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Infographic option (PDF/DOCX only) */}
        {showInfographicOption && (
          <div className="px-6 py-3 border-b border-dark-border">
            {hasInfographic ? (
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeInfographic}
                  onChange={e => setIncludeInfographic(e.target.checked)}
                  className="rounded border-gray-600 bg-dark-bg text-indigo-500 focus:ring-indigo-500"
                />
                Include role infographic
              </label>
            ) : (
              <div className="flex items-center gap-3 text-sm text-gray-400">
                <span>No infographic generated yet.</span>
                <button
                  onClick={handleGenerateInfographic}
                  disabled={generatingInfographic}
                  className="text-indigo-400 hover:text-indigo-300 underline disabled:opacity-50"
                >
                  {generatingInfographic ? 'Generating...' : 'Generate now'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Content area */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && format === 'md' ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-500 border-t-transparent" />
              <span className="ml-3 text-gray-400">Generating JD...</span>
            </div>
          ) : error ? (
            <div className="text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg p-4">
              {error}
              <button
                onClick={() => { setError(''); fetchPreview(template); }}
                className="block mt-2 text-indigo-400 hover:text-indigo-300 text-sm underline"
              >
                Retry
              </button>
            </div>
          ) : format === 'md' ? (
            <pre className="text-sm text-dark-text whitespace-pre-wrap font-mono leading-relaxed bg-dark-bg border border-dark-border rounded-lg p-4">
              {markdown}
            </pre>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <svg className="w-12 h-12 mb-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-sm">
                Ready to download as <span className="text-white font-medium">{FORMAT_LABELS[format]}</span>
              </p>
              {markdown && (
                <p className="text-xs text-gray-500 mt-1">
                  Based on the {template} template
                  {includeInfographic && hasInfographic ? ' with infographic' : ''}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-dark-border">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 transition-colors"
          >
            Close
          </button>
          {format === 'md' && (
            <button
              onClick={handleCopy}
              disabled={loading || !!error}
              className="px-4 py-2 bg-dark-bg border border-dark-border hover:border-indigo-500 text-gray-300 hover:text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {copied ? 'Copied!' : 'Copy Markdown'}
            </button>
          )}
          <button
            onClick={handleDownload}
            disabled={loading || (format === 'md' && !!error)}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {loading && format !== 'md' ? 'Exporting...' : `Download .${format}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JDExportModal;
